import { Injectable, NgZone, Inject } from '@angular/core';
import { IOptions } from 'animated-scroll-to';
import animateScrollTo from 'animated-scroll-to';
import { Router, Scroll } from '@angular/router';
import { takeUntil, filter, map, tap } from 'rxjs/operators';
import { Destroyer, sleep } from '@apps/tools';
import { WindowRef } from './window.service';
import { DOCUMENT } from '@angular/common';

/*
 * Easing Functions
 * https://gist.github.com/gre/1650294
 */
export const EasingFunctions = {
  // no easing, no acceleration
  linear: t => {
    return t;
  },
  // accelerating from zero velocity
  easeInQuad: t => {
    return t * t;
  },
  // decelerating to zero velocity
  easeOutQuad: t => {
    return t * (2 - t);
  },
  // acceleration until halfway, then deceleration
  easeInOutQuad: t => {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  },
  // accelerating from zero velocity
  easeInCubic: t => {
    return t * t * t;
  },
  // decelerating to zero velocity
  easeOutCubic: t => {
    return --t * t * t + 1;
  },
  // acceleration until halfway, then deceleration
  easeInOutCubic: t => {
    return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
  },
  // accelerating from zero velocity
  easeInQuart: t => {
    return t * t * t * t;
  },
  // decelerating to zero velocity
  easeOutQuart: t => {
    return 1 - --t * t * t * t;
  },
  // acceleration until halfway, then deceleration
  easeInOutQuart: t => {
    return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t;
  },
  // accelerating from zero velocity
  easeInQuint: t => {
    return t * t * t * t * t;
  },
  // decelerating to zero velocity
  easeOutQuint: t => {
    return 1 + --t * t * t * t * t;
  },
  // acceleration until halfway, then deceleration
  easeInOutQuint: t => {
    return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t;
  }
};

const repeatsLimit = 5;
const scrollOptions: IOptions = {
  maxDuration: 3000,
  minDuration: 250,
  speed: 500,
  easing: EasingFunctions.easeInOutQuint
};

const getQueryParam = (name: string, url?: string): string => {
  const urlParams = new URLSearchParams(url || window.location.search);
  return urlParams.get(name);
};

export type ScrollBehavior = 'auto' | 'smooth';

@Injectable({
  providedIn: 'root'
})
export class ScrollService extends Destroyer {
  private timer;
  private counter = 0;

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private winRef: WindowRef,
    @Inject(DOCUMENT) private document: Document
  ) {
    super();
  }

  public initRouterScroll(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter(e => e instanceof Scroll),
        map((e: any) => ({ anchor: e.anchor, first: e.routerEvent.id === 1 }))
      )
      .subscribe(({ anchor, first }) => {
        if (!anchor) {
          this.scrollTop();
        } else {
          this.scrollToElement(`#${anchor}`, first ? 500 : 200, {
            verticalOffset: this.getFixedMenuOffset()
          });
        }
      });
  }

  public scrollTop(behavior: ScrollBehavior = 'auto') {
    this._scrollToTop(behavior);
  }

  async scrollToElement(
    element: string | Element,
    delay = 0,
    options: IOptions = {}
  ) {
    if (delay > 0) {
      await sleep(delay);
    }

    if (typeof element === 'string') {
      element = document.querySelector(element);
    }
    if (element) {
      this.counter = 0;
      return await this._scrollToElement(element, options);
    } else if (this.counter < repeatsLimit) {
      this.counter++;
      clearTimeout(this.timer);
      this.ngZone.runOutsideAngular(() => {
        this.timer = setTimeout(() => {
          this.scrollToElement(element, 0, options);
        }, 500);
      });
    } else {
      this.counter = 0;
      return false;
    }
  }

  scrollByQueryParams(offset?: number, url?: string): string {
    const params = (getQueryParam('scroll', url) || '').split(',');
    let element;
    let param;
    for (const p of params) {
      element = document.querySelector(`[data-id="${ p }"]`);
      param = p;
      if (element) {
        break;
      }
    }

    this.scrollToElement(element, 500, {
      verticalOffset: offset || 0,
      speed: 800,
      easing: EasingFunctions.easeInOutCubic
    });

    return param;
  }

  private _scrollToTop(behavior: ScrollBehavior) {
    if (this.winRef.nativeWindow.scrollTo) {
      this.winRef.nativeWindow.scrollTo({
        top: 0,
        behavior
      });
    }
  }

  private async _scrollToElement(
    element: Element,
    options: IOptions
  ): Promise<any> {
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    await animateScrollTo(
      elementPosition,
      Object.assign(scrollOptions, options)
    );
  }

  private getFixedMenuOffset(): number {
    const isFixed = this.document.body.classList.contains('menu-fixed');
    return isFixed ? -50 : 0;
  }
}
